import { uniqBy, sortBy } from 'lodash';

import actions from '../../constants/actionTypes';
import {
  EnumChatAppType,
  EnumUserChatRole,
  findEnumById,
} from '../../constants/enum';
import { isEmptyObject, parseDateTime } from '../../utils/utils';

const initialChatTabData = (enumTab) => {
  const userSeenStatus = enumTab.userRoles.reduce(
    (seenStatus, role) => ({
      ...seenStatus,
      [role.id]: {
        lastMessageId: null,
        userRole: role.id,
      },
    }),
    {},
  );
  return {
    hasUnreadMessage: false,
    hasMore: false,
    isLoading: false,
    draftMessage: '',
    userSeenStatus,
    messages: [],
  };
};
// - each message should have format like this:
// objects
//   required uint64 id
//   required string sendTime
//   required string message
//   required boolean isDeleted
//   required boolean isEdited
//   optional string clientMessageId
//   required object sender
//     required uint32 id
//     required uint8 userRole --> USER(1), SHIPPER(2), CALL_CENTER(3)
//     required string name
//     optional string image

const initialChatChannelData = (appType, id) => {
  const chatAppTypeEnum = findEnumById(EnumChatAppType, appType);
  const chatTabData = chatAppTypeEnum.listChatTab.reduce(
    (data, tab) => ({
      ...data,
      [tab.id]: initialChatTabData(tab),
    }),
    {},
  );
  return {
    id,
    appType,
    isDisabled: false,
    isOpenFull: false,
    isChatTextFocus: false,
    currentChatTab: chatAppTypeEnum.defaultChatTab,
    chatTabData,
  };
};

const initialState = {
  activeChannel: -1,
  chatChannelListOrder: [],
  chatChannelData: {},
};

function changeChatTabData(state, id, tab, newData) {
  const { chatChannelData } = state;
  if (!isEmptyObject(chatChannelData[id])) {
    const { chatTabData } = chatChannelData[id];
    return {
      ...state,
      chatChannelData: {
        ...chatChannelData,
        [id]: {
          ...chatChannelData[id],
          chatTabData: {
            ...chatTabData,
            [tab]: {
              ...chatTabData[tab],
              ...newData,
            },
          },
        },
      },
    };
  }
  return state;
}

function changeChatChannelData(state, id, newData) {
  const { chatChannelData } = state;
  if (!isEmptyObject(chatChannelData[id])) {
    return {
      ...state,
      chatChannelData: {
        ...chatChannelData,
        [id]: {
          ...chatChannelData[id],
          ...newData,
        },
      },
    };
  }
  return state;
}

function appendNewMessage(messageList, newMessage) {
  let mergedMessageList = messageList;
  if (newMessage.clientMessageId) {
    mergedMessageList = messageList.map((messageItem) => {
      if (messageItem.clientMessageId === newMessage.clientMessageId) {
        return {
          ...messageItem,
          ...newMessage,
        };
      }
      return messageItem;
    });
  }

  return sortBy(
    uniqBy(
      [...mergedMessageList, newMessage],
      item => item.id || item.clientMessageId,
    ),
    item => parseDateTime(item.sendTime),
  );
}

function chatBoxReducer(state = initialState, action) {
  const { response, type } = action;
  switch (type) {
    case actions.SELECT_CHAT_CHANNEL: {
      const { id } = response;
      return {
        ...state,
        activeChannel: id,
      };
    }
    case actions.OPEN_CHAT_CHANNEL: {
      const { id, isHidden } = response;
      const { chatChannelListOrder } = state;
      return {
        ...changeChatChannelData(state, id, {
          isOpenFull: true,
        }),
        activeChannel: id,
        chatChannelListOrder: isHidden
          ? [...chatChannelListOrder.filter(channelId => channelId !== id), id]
          : chatChannelListOrder,
      };
    }
    case actions.CLOSE_CHAT_CHANNEL: {
      const { id } = response;
      const { activeChannel, chatChannelListOrder } = state;
      const newChatChannelListOrder = chatChannelListOrder.filter(
        channelId => channelId !== id,
      );
      return {
        ...state,
        ...changeChatChannelData(state, id, {
          isOpenFull: false,
        }),
        activeChannel:
          activeChannel === id
            ? newChatChannelListOrder[0] || -1
            : activeChannel,
        chatChannelListOrder: newChatChannelListOrder,
      };
    }
    case actions.MINIFY_CHAT_CHANNEL: {
      const { id } = response;
      const { activeChannel } = state;
      return {
        ...changeChatChannelData(state, id, {
          isOpenFull: false,
        }),
        activeChannel: activeChannel === id ? -1 : activeChannel,
      };
    }
    case actions.FOCUS_CHAT_CHANNEL_INPUT: {
      const { id } = response;
      return changeChatChannelData(state, id, {
        isChatTextFocus: true,
      });
    }
    case actions.BLUR_CHAT_CHANNEL_INPUT: {
      const { id } = response;
      return changeChatChannelData(state, id, {
        isChatTextFocus: false,
      });
    }
    case actions.CHANGE_DRAFT_MESSAGE: {
      const { id, tab, message } = response;
      return changeChatTabData(state, id, tab, {
        draftMessage: message,
      });
    }
    case actions.SELECT_CHAT_TAB: {
      const { id, tab } = response;
      return changeChatChannelData(state, id, {
        currentChatTab: tab,
      });
    }
    case actions.START_LOADING_MESSAGE: {
      const { id, tab } = response;
      return changeChatTabData(state, id, tab, {
        isLoading: true,
      });
    }
    case actions.STOP_LOADING_MESSAGE: {
      const { id, tab } = response;
      return changeChatTabData(state, id, tab, {
        isLoading: false,
      });
    }
    case actions.SEND_MESSAGE_SUCCESS: {
      const {
        id, tab, messageId, clientMessageId,
      } = response;
      const { chatChannelData } = state;
      const { messages } = chatChannelData[id].chatTabData[tab];

      return changeChatTabData(state, id, tab, {
        messages: messages.map(messageItem =>
          (messageItem.clientMessageId === clientMessageId
            ? {
              id: messageId,
              ...messageItem,
            }
            : messageItem),
        ),
      });
    }
    case actions.SEND_MESSAGE_FAIL: {
      const { id, tab, clientMessageId } = response;
      const { chatChannelData } = state;
      const { messages } = chatChannelData[id].chatTabData[tab];
      return changeChatTabData(state, id, tab, {
        messages: messages.map(messageItem =>
          (messageItem.clientMessageId === clientMessageId
            ? {
              ...messageItem,
              clientMessageId: -clientMessageId,
            }
            : messageItem),
        ),
      });
    }
    case actions.DELETE_MESSAGE: {
      const { id, tab, messageId } = response;
      const { chatChannelData } = state;
      const { messages } = chatChannelData[id].chatTabData[tab];
      return changeChatTabData(state, id, tab, {
        messages: messages.map(messageItem =>
          (messageItem.id === messageId
            ? {
              ...messageItem,
              isDeleted: true,
            }
            : messageItem),
        ),
      });
    }
    case actions.EDIT_MESSAGE: {
      const {
        id, tab, messageId, newMessage,
      } = response;
      const { chatChannelData } = state;
      const { messages } = chatChannelData[id].chatTabData[tab];
      return changeChatTabData(state, id, tab, {
        messages: messages.map(messageItem =>
          (messageItem.id === messageId
            ? {
              ...messageItem,
              message: newMessage,
              isEdited: true,
            }
            : messageItem),
        ),
      });
    }
    case actions.UPDATE_USER_SEEN_STATUS: {
      const {
        id, tab, userRole, lastMessageId,
      } = response;
      const { chatChannelData } = state;
      const { userSeenStatus, hasUnreadMessage } = chatChannelData[
        id
      ].chatTabData[tab];
      return changeChatTabData(state, id, tab, {
        userSeenStatus: {
          ...userSeenStatus,
          [userRole]: {
            ...userSeenStatus[userRole],
            lastMessageId,
          },
        },
        hasUnreadMessage:
          userRole !== EnumUserChatRole.USER.id ? hasUnreadMessage : false,
      });
    }
    case actions.RECEIVE_MESSAGE_LIST: {
      const {
        id,
        tab,
        messages,
        userSeenStatus,
        hasUnreadMessage,
        hasMore,
      } = response;
      const { chatChannelData } = state;
      if (!isEmptyObject(chatChannelData[id])) {
        const {
          messages: currentMessages,
          userSeenStatus: currentUserSeenStatus,
          hasUnreadMessage: currentHasUnreadMessage,
        } = chatChannelData[id].chatTabData[tab];
        return changeChatTabData(state, id, tab, {
          hasUnreadMessage:
            hasUnreadMessage !== null
              ? hasUnreadMessage
              : currentHasUnreadMessage,
          hasMore,
          messages: sortBy(
            uniqBy(
              [...messages, ...currentMessages],
              item => item.id || item.clientMessageId,
            ),
            item => parseDateTime(item.sendTime),
          ),
          userSeenStatus: {
            ...currentUserSeenStatus,
            ...userSeenStatus,
          },
        });
      }
      return state;
    }
    case actions.RECEIVE_NEW_MESSAGE: {
      const {
        message, isNewMessage, id, tab,
      } = response;
      const { activeChannel, chatChannelData, chatChannelListOrder } = state;
      if (!isEmptyObject(chatChannelData[id])) {
        const newState = chatChannelListOrder.includes(id)
          ? state
          : {
            ...state,
            activeChannel: activeChannel === -1 ? id : activeChannel,
            chatChannelListOrder: [id, ...chatChannelListOrder],
          };
        const currentMessages = chatChannelData[id].chatTabData[tab].messages;
        return changeChatTabData(newState, id, tab, {
          messages: appendNewMessage(currentMessages, message),
          hasUnreadMessage: isNewMessage,
        });
      }
      return state;
    }
    case actions.CLEAR_UNREAD_MESSAGE: {
      const { id, tab, userSeenStatus } = response;
      return changeChatTabData(state, id, tab, {
        hasUnreadMessage: false,
        userSeenStatus,
      });
    }
    case actions.INIT_CHAT_CHANNEL: {
      const { appType, id, isDisabled } = response;
      const { activeChannel, chatChannelListOrder, chatChannelData } = state;
      if (isEmptyObject(chatChannelData[id])) {
        return {
          ...state,
          activeChannel: activeChannel === -1 ? id : activeChannel,
          chatChannelListOrder:
            chatChannelListOrder.findIndex(item => item === id) !== -1
              ? chatChannelListOrder
              : [id, ...chatChannelListOrder],
          chatChannelData: {
            ...chatChannelData,
            [id]: {
              ...initialChatChannelData(appType, id),
              isDisabled,
            },
          },
        };
      }
      return state;
    }
    default: {
      return state;
    }
  }
}

export default chatBoxReducer;



// WEBPACK FOOTER //
// ./foody/src/pages/ChatBox/reducers.js